import { api } from "./client";
import type {
  PrequalBuyerDocumentPath,
  PrequalificationDocumentDto,
  PrequalificationOutcome,
  PrequalScheduleFiguresDto,
} from "./prequalification";
import type {
  PrequalBuyerType,
  PrequalCashEquivalents,
  PrequalEquitySource,
  PrequalInternalRelationship,
  PrequalOperatorArrangement,
} from "@/portal/api/prequalification";

// Wire mirror of `Core/DTOs/Buyers/BuyerPrequalCallDtos.cs` — one call: the
// qualification interview and the prequalification as a single telephone call
// on the buyer record, with one send at the end (owner brief, 2026-10-16).
// Routes: `api/buyers/{id}/prequal-call`, all `Buyers.Update` + ownership.

/** The prequalification answers taken on the call, in the funnel's own shape.
 *  Every field is optional and absent/null means "not asked yet" — unlike the
 *  funnel's answers, whose defaults cannot tell a skipped question from an
 *  answer. Geography and industry are NOT here: they are asked as interview
 *  questions (GEO1/GEO2/GEO3) and the funnel's fields are derived from them. */
export interface BuyerCallAnswers {
  // Path and deal
  documentPath?: PrequalBuyerDocumentPath | null;
  listingId?: string | null;
  targetBusinessName?: string | null;
  targetPurchasePrice?: number | null;
  targetAnnualCashFlow?: number | null;
  /** `YYYY-MM-DD` — a wall-calendar date. */
  targetPurchaseDate?: string | null;
  priceBand?: string | null;
  internalRelationship?: PrequalInternalRelationship | null;
  internalYearsWithBusiness?: number | null;
  internalCoBuyerCount?: number | null;
  // Who is buying
  buyerType?: PrequalBuyerType | null;
  acquiringCompanyName?: string | null;
  acquiringCompanyTitle?: string | null;
  acquiringCompanyWebsite?: string | null;
  acquiringCompanyAbout?: string | null;
  equitySource?: PrequalEquitySource | null;
  operatorArrangement?: PrequalOperatorArrangement | null;
  operatorName?: string | null;
  // Capital
  cashAvailable?: number | null;
  /** The buyer would not share a cash figure (FC2 "won't share"). */
  cashWontShare?: boolean;
  cashEquivalents?: PrequalCashEquivalents | null;
  reservesAmount?: number | null;
  reservesBand?: string | null;
  // Background
  ownershipExperience?: string | null;
  /** "Owns it now?" — asked when they have owned a business. */
  ownsNow?: boolean | null;
  managementExperience?: string | null;
  industryFit?: string | null;
  /** "Has researched it?" — asked when the industry is new to them. */
  hasResearchedIndustry?: boolean | null;
  involvement?: string | null;
  aamaPath?: boolean | null;
  // Eligibility
  knockouts?: Record<string, string> | null;
  goals?: string | null;
  // Contact and consent
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  /** The consultant read the consent statement aloud; the buyer agreed. */
  consentAccepted?: boolean;
}

export type BuyerPrequalCallReviewStatus =
  | "awaitingReview" | "sent" | "declineSent" | "withheld";

export interface BuyerPrequalCallSubmissionDto {
  submissionId: string;
  referenceNumber?: string | null;
  outcome: PrequalificationOutcome;
  qualifiedAmount?: number | null;
  completedAtUtc: string;
  reviewStatus: BuyerPrequalCallReviewStatus;
}

export interface BuyerPrequalCallStateDto {
  buyerLeadId: string;
  /** Null when the buyer has never been interviewed. */
  interviewId?: string | null;
  interviewVersion?: number | null;
  interviewStatus?: "InProgress" | "Completed" | null;
  /** Stored answers, with contact prefilled from the buyer record where blank. */
  answers: BuyerCallAnswers;
  /** The published buyer consent line, read aloud by the consultant. */
  consentScript: string;
  /** The published call certification sentence. */
  certificationText: string;
  canPrequalify: boolean;
  /** Why Prequalify is disabled, in plain words. */
  blockedReasons: string[];
  latestCallPrequal?: BuyerPrequalCallSubmissionDto | null;
  /** The published Phoenix Default wording of each funnel-copy block the call
   *  shows (Document Language editor), keyed by block key — published, else the
   *  code default (M1). */
  funnelCopy?: Record<string, string> | null;
}

export interface BuyerPrequalCallResultDto {
  submissionId: string;
  outcome: PrequalificationOutcome;
  qualifiedAmount?: number | null;
  referenceNumber?: string | null;
}

export interface BuyerPrequalCallAttachmentDto {
  fileName: string;
  sizeBytes?: number | null;
}

export interface BuyerPrequalCallEmailPreviewDto {
  kind: "educationPrequal" | "decline";
  toEmail: string;
  toName?: string | null;
  subject: string;
  body: string;
  attachments: BuyerPrequalCallAttachmentDto[];
  canSend: boolean;
  blockedReason?: string | null;
}

export interface BuyerPrequalCallReviewDto {
  submissionId: string;
  referenceNumber?: string | null;
  validThroughUtc?: string | null;
  outcome: PrequalificationOutcome;
  qualifiedAmount?: number | null;
  supportablePriceLow?: number | null;
  supportablePriceHigh?: number | null;
  documentPath?: PrequalBuyerDocumentPath | null;
  documents: PrequalificationDocumentDto[];
  reviewStatus: BuyerPrequalCallReviewStatus;
  decidedAtUtc?: string | null;
  decidedByName?: string | null;
  /** Null once a decision has been made. */
  email?: BuyerPrequalCallEmailPreviewDto | null;
  /** prequal-v7: the two payment schedules, 20-year first (staff view). Absent
   *  before v7 and on a result with no free cash flow to size. */
  schedules?: PrequalScheduleFiguresDto[] | null;
}

export interface BuyerPrequalCallSendResultDto {
  sentAtUtc: string;
  toEmail: string;
  attachmentNames: string[];
}

export const prequalCallApi = {
  getState: (buyerId: string) =>
    api.get<BuyerPrequalCallStateDto>(`/buyers/${buyerId}/prequal-call`),

  /** Autosave (whole-object replace). */
  saveAnswers: (buyerId: string, answers: BuyerCallAnswers) =>
    api.put<BuyerPrequalCallStateDto>(`/buyers/${buyerId}/prequal-call/answers`, { answers }),

  /** Run the prequalification. Generates documents and emails nothing. A 409
   *  means a live invitation already exists: confirm, then retry with
   *  `confirmDuplicate: true`. */
  prequalify: (buyerId: string, body: { confirmDuplicate?: boolean } = {}) =>
    api.post<BuyerPrequalCallResultDto>(`/buyers/${buyerId}/prequal-call/prequalify`, body),

  getReview: (buyerId: string, submissionId: string) =>
    api.get<BuyerPrequalCallReviewDto>(
      `/buyers/${buyerId}/prequal-call/review/${submissionId}`),

  /** Pass: the combined email. Decline: the decline documents. */
  send: (buyerId: string, submissionId: string, body: { subject?: string; body?: string } = {}) =>
    api.post<BuyerPrequalCallSendResultDto>(
      `/buyers/${buyerId}/prequal-call/review/${submissionId}/send`, body),

  /** Decline only: record "Don't send". */
  withhold: (buyerId: string, submissionId: string) =>
    api.post<BuyerPrequalCallReviewDto>(
      `/buyers/${buyerId}/prequal-call/review/${submissionId}/withhold`, {}),
};
